import auth0 from 'auth0-js';
import { jwtDecode } from "jwt-decode";
import Api from '../../Api';
import externalApps from '../../externalApps/ExternalApps';
import TokenApi from '../../tokenApis';

export class AuthZeroProvider {

  constructor(authConfig) {
    this.client = new auth0.WebAuth({
      domain: authConfig.authority,
      clientID: authConfig.clientId,
      redirectUri: window.location.origin + '/callback',
      responseType: 'id_token token',
      scope: 'openid profile profile_hrbAuth'
    });  
  }

  /**
 * Logs in a user with the specified language preference.
 * @param {string} _language - The language preference during login.
 */
  login(_language) {
    this.client.authorize({ prompt: 'login' });
  }

  signup() {
    this.client.authorize({
      state: 'register',
      nonce: 'abc12',
      screen_hint: 'signup'
    });
  }

  /**
 * @param {function(result: any, err: any): void} callback
 */
  silentAuthentication = (callback) => {
    this.client.checkSession({}, (err, authResult) => {
      if (authResult) {
          console.log(authResult);
      } else if (err) {
          const options = callback();
          console.log(err);
          this.client.authorize({ prompt: 'none', language: options.language });
      }
    });
  }

  /**
  * One Time Code for authenticating the user
  * @param {string} _code - One Time Code
  * @param {string} _userName - User Name
  */
  otcAuthentication(_code, _userName) {};

  /**
  * Logs out the current user.
  *  
  */
  logout() { 
    if (localStorage.getItem('myb_heap_app_id') && window.heap) {
      window.heap.resetIdentity();
    }
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('expires_at');
    this.client.logout({
      returnTo: window.location.origin + '/logout'
    });
  }

  /**
  * Passes the Username
  * @param {string} _userName
  */
  passUserName(_userName) {};

  /** 
  * Handles the authentication process.
  */
  handleAuthentication = async() => {
    var hashStr = window.location.hash;

    if (hashStr.length === 0) {
        hashStr = window.location.search;
    }

    hashStr = hashStr.replace(/^#?\/?/, '');
    var queryString = require('simple-query-string');

    var authResult = queryString.parse(hashStr);

    if(authResult.code) {
      return this.isAuthenticated() ? this.getTokenFromSessionStorage(authResult.state) 
                                    : this.getTokenByAuthCode(authResult.code, authResult.state);
    }
    this.storage = sessionStorage;
    var storage = this.storage;
    var expiresAt = JSON.stringify(
        authResult.expires_in * 1000 + new Date().getTime()
    );

    storage.setItem('access_token', authResult.access_token);
    storage.setItem('id_token', authResult.id_token);
    storage.setItem('expires_at', expiresAt);

    var token = this.decodeJWT(authResult.id_token);

    return {
        accessToken: authResult.access_token || null,
        idToken: authResult.id_token || null,
        idTokenPayload: token || null,
        state: authResult.state || null,
        expiresIn: authResult.expires_in ? parseInt(authResult.expires_in, 10) : null,
        tokenType: authResult.token_type || null,
        scope: authResult.scope || null,
        lang: authResult.lang || null,
        ga: authResult._ga || null,
        userAccountId: token.userAccountId,
        first_name: token.first_name,
        last_name: token.last_name,
        email: token.name,
        province: token.province,
        phone: token.phone
    };
  };

  getAccessToken() {
    var accessToken = this.getToken();
    if (!accessToken) {
        throw new Error('No access token found');
    }
    return accessToken;
  }

  getProfile() {
    if (this.isAuthenticated()) {
      return this.decodeJWT(this.getToken());
    }
    return {};
  }

  getTokenTimeToLive() {
    return this.getExpiresAt() - new Date().getTime();
  }

  isAuthenticated() {
    return new Date().getTime() < this.getExpiresAt();
  }
  
  redirect(app, route) {
    var appInfo = externalApps.getAppInfo(app);
    var state = { source: 'RTE', route: route };
    var encodedState = this.encode(state);
    this.client.authorize({ 
      responseType:'code',
      state: encodedState,
      clientID: appInfo.clientId,
      redirectUri: appInfo.callbackUrl,
      scope: 'openid profile profile_hrbAuth'
    });
  }

  decodeJWT = (token) => {
    try {
        const decoded = jwtDecode(token);
        return decoded;
    } catch (err) {
        console.error('Error decoding JWT:', err);
        return null;
    }
  };

  getExpiresAt() {
    return JSON.parse(sessionStorage.getItem('expires_at'));
  }

  getToken() {
    return sessionStorage.getItem('id_token');
  }

  getTokenFromSessionStorage(state) {
    var authResult = {
      access_token: sessionStorage.getItem('access_token'),
      id_token: sessionStorage.getItem('id_token'),
      expires_in: sessionStorage.getItem('expires_at'),
      state: state
    };

    return this.getTokenFromAuthResult(authResult);
  }

  getTokenByAuthCode(code, state) {
    return TokenApi.create().getToken(code).then(response => {
      var authResult = response.data;
      if(authResult.access_token) {
        var expiresAt = JSON.stringify(authResult.expires_in * 1000 + new Date().getTime());

        var storage = sessionStorage;
        storage.setItem('access_token', authResult.access_token);
        storage.setItem('id_token', authResult.id_token);
        storage.setItem('expires_at', expiresAt);

        authResult.state = state;

        return this.getTokenFromAuthResult(authResult);
      }
    },
    error => {
      console.log(error);
    });
  }

  getTokenFromAuthResult(authResult) {
    var data = this.decode(authResult.state);
    if(data.route) {
      sessionStorage.setItem("myblock_route", data.route);
    }
    if(data.source) {
      sessionStorage.setItem("myblock_source_app", data.source)
    }

    var token = this.decodeJWT(authResult.id_token);

    return {
        accessToken: authResult.access_token || null,
        idToken: authResult.id_token || null,
        idTokenPayload: token || null,
        state: authResult.state || null,
        expiresIn: authResult.expires_in ? parseInt(authResult.expires_in, 10) : null,
        tokenType: authResult.token_type || null,
        scope: authResult.scope || null,
        lang: authResult.lang || null,
        ga: authResult._ga || null,
        userAccountId: token.userAccountId,
        first_name: token.first_name,
        last_name: token.last_name,
        email: token.name,
        province: token.province,
        phone: token.phone
    }; 
  }

  encode(state) {
    return encodeURIComponent(btoa(JSON.stringify(state)));
  }

  decode(state) {
    return JSON.parse(atob(decodeURIComponent(state)));
  }
}